import { subHours, isAfter } from 'date-fns';

import dayjs from 'utils/extendedDayJS';
import { guessTimezone, convertToTimezone } from 'utils/dateTimeUtils';
import { EVENT_VIEW_DATE_FORMAT } from 'constants/dates';
import { EditableEvent, EventType, PublishedEvent } from 'types/event.types';

/**
 * Formats the location string for an event based on its type and location settings
 *
 * @param event - The event object containing location information
 * @returns A formatted string representing the event's location
 *
 * For single events:
 * - Returns the location address if in-person only
 * - Returns "Online" if virtual only
 * - Returns "Online • [address]" if both virtual and in-person
 * - Returns "No location yet" if no address is specified
 *
 * For multi-session events:
 * - Returns the common location address if all in-person sessions share the same location
 * - Returns "Multiple locations" if in-person sessions have different locations
 * - Follows single event logic if only virtual sessions exist
 */
export const formatEventLocation = (event: Partial<PublishedEvent>): string => {
  let { isInPerson = false, isVirtual = false } = event;
  const { sessions = [], locationAddress = '', eventType } = event;

  // Handle multi-session events
  if (eventType === EventType.MULTI_SESSIONS && sessions && sessions.length > 0) {
    isInPerson = sessions.some((session) => session.isInPerson) || false;
    isVirtual = sessions.some((session) => session.isVirtual) || false;

    if (isInPerson) {
      const firstSessionAddress = sessions[0].locationAddress || '';
      const allSameLocation = sessions
        .filter((session) => session.isInPerson)
        .every((session) => (session.locationAddress || '').trim() === firstSessionAddress.trim());

      return allSameLocation ? firstSessionAddress : 'Multiple locations';
    }
  }

  // Handle single events
  const formattedAddress = locationAddress?.trim() || 'No location yet';

  if (isInPerson && isVirtual) {
    return `Online • ${formattedAddress}`;
  }

  return isInPerson ? formattedAddress : 'Online';
};

/**
 * Formats the date string for an event based on its start and end dates
 *
 * @param event - The event object containing datetime information
 * @returns A formatted string representing the event's date(s)
 *
 * If start and end dates are on the same day:
 * - Returns a single date string in EVENT_VIEW_DATE_FORMAT
 *
 * If start and end dates are on different days:
 * - Returns a date range string with both dates in EVENT_VIEW_DATE_FORMAT
 * separated by an en dash
 */
export const getEventDateString = (
  event: Partial<PublishedEvent> | Partial<EditableEvent>
): string => {
  if (!event.datetimeScheduled || !event.datetimeEnd) return '';

  const startDatetime = dayjs
    .utc(event.datetimeScheduled)
    .tz(event.allDay ? event.timezoneCode : dayjs.tz.guess());
  const endDatetime = dayjs
    .utc(event.datetimeEnd)
    .tz(event.allDay ? event.timezoneCode : dayjs.tz.guess());

  if (startDatetime.isSame(endDatetime, 'day')) {
    return startDatetime.format(EVENT_VIEW_DATE_FORMAT);
  }

  return `${startDatetime.format(EVENT_VIEW_DATE_FORMAT)} – ${endDatetime.format(
    EVENT_VIEW_DATE_FORMAT
  )}`;
};

/**
 * Formats the time string for an event based on its start and end times
 *
 * @param event - The event object containing datetime information
 * @returns A formatted string representing the event's time(s)
 *
 * For all-day events:
 * - Returns "All day"
 *
 * For same-day events:
 * - Returns time range in format "1:00 PM – 2:00 PM EDT"
 *
 * For multi-day events:
 * - Returns time range with dates in format "Jan 1, 1:00 PM – Jan 2, 2:00 PM EDT"
 */
export const getEventTimeString = (
  event: Partial<PublishedEvent> | Partial<EditableEvent>
): string => {
  const startDatetime = dayjs
    .utc(event.datetimeScheduled)
    .tz(event.allDay ? event.timezoneCode : dayjs.tz.guess());
  const endDatetime = dayjs
    .utc(event.datetimeEnd)
    .tz(event.allDay ? event.timezoneCode : dayjs.tz.guess());

  return dayjs.formatEventTime({
    startDatetime,
    endDatetime,
    allDay: event.allDay,
    displayedTimezone: dayjs.tz.guess() || event.timezoneCode || 'UTC'
  });
};

/**
 * Checks if the event is starting in one hour
 * @param event - The event object containing datetime information
 * @returns True if the event is starting in one hour, false otherwise
 */
export const isEventStartingInOneHour = (
  event: Partial<PublishedEvent> | Partial<EditableEvent>
): boolean => {
  if (!event.datetimeScheduled) return false;

  const timezone = event.timezoneCode || guessTimezone();
  const eventStartTime = convertToTimezone(event.datetimeScheduled, timezone);

  if (event.allDay) {
    eventStartTime.setHours(0, 0, 0, 0);
  }

  const now = convertToTimezone(new Date(), timezone);
  return isAfter(now, subHours(eventStartTime, 1));
};
